import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";

import { Space, Breadcrumb, Button, Select, Modal, Input, message } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { SaveOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";

import { sub } from "date-fns";
import type { RadioChangeEvent } from "antd";

import { ResponsiveGridLayout } from 'react-grid-layout';
import MetricsAggregations from "../../components/MetricsAggregations";
import GraphWidget from "./widgets/GraphWidget";
import GaugeWidget from "./widgets/GaugeWidget";
import SessionStore from "../../model/SessionStore";
import { DashboardConfig, DashboardWidget } from "../../model/model";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";


function Dashboard() {
  const [metricsAggregator, setMetricsAggregator] = useState<string>('AVG');
  const [metricsDownsampling, setMetricsDownsampling] = useState<string>('1m');
  const [startDate, setStartDate] = useState<Date>(sub(new Date(), { hours: 6 }));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [timeInterval, setTimeInterval] = useState<string>('6h');
  const [widgets, setWidgets] = useState<Array<{i: string, x: number, y: number, w: number, h: number, type: string, config: any}>>([]);
  const [nextId, setNextId] = useState(1);
  const [dashboards, setDashboards] = useState<DashboardConfig[]>([]);
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | undefined>(undefined);
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [saveDashboardName, setSaveDashboardName] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const skipLayoutUpdate = useRef(false);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(1200);
  const rowHeight = 30;

  const containerHeight = useMemo(() => {
    if (widgets.length === 0) return 780;
    const maxBottom = widgets.reduce((max, w) => Math.max(max, w.y + w.h), 0);
    return Math.max(780, Math.ceil(maxBottom * rowHeight * 1.3));
  }, [widgets]);

  useEffect(() => {
    loadDashboards();
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      if (gridContainerRef.current) {
        setGridWidth(gridContainerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const loadDashboards = () => {
    SessionStore.getClient().getAll(SessionStore.getBearer()).then((resp) => {
      const sorted = (resp.data || []).sort((a: DashboardConfig, b: DashboardConfig) => b.lastModified - a.lastModified);
      setDashboards(sorted);
      if (sorted.length > 0) {
        loadDashboard(sorted[0].id);
      }
    }).catch(() => {
      setDashboards([]);
    });
  };

  const loadDashboard = (dashboardId: string) => {
    SessionStore.getClient().getById(dashboardId, SessionStore.getBearer()).then((resp) => {
      const dc: DashboardConfig = resp.data;
      setSelectedDashboardId(dc.id);
      setWidgets(dc.widgets.map((w: DashboardWidget) => ({
        i: w.id,
        x: w.x,
        y: w.y,
        w: w.w,
        h: w.h,
        type: w.type,
        config: w.config || {}
      })));
      const maxId = dc.widgets.reduce((max: number, w: DashboardWidget) => {
        const num = parseInt(w.id);
        return num > max ? num : max;
      }, 0);
      setNextId(maxId + 1);
    });
  };

  const onMetricsAggregationChange = (e: RadioChangeEvent) => {
    setMetricsAggregator(e.target.value);
  };

  const onMetricsDownsamplingChange = (e: RadioChangeEvent) => {
    setMetricsDownsampling(e.target.value);
  };


  const loadMetrics = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  const onLayoutChange = (currentLayout: any) => {
    if (skipLayoutUpdate.current) return;
    setWidgets(widgets.map(widget => {
      const item = currentLayout.find((l: any) => l.i === widget.i);
      return item ? {...widget, x: item.x, y: item.y, w: item.w, h: item.h} : widget;
    }));
  };

  const addWidget = (type: string) => {
    const newWidget = {i: nextId.toString(), x: 0, y: 0, w: 6, h: 4, type, config: {}};
    const shiftedWidgets = widgets.map(w => ({...w, y: w.y + 4}));
    skipLayoutUpdate.current = true;
    setWidgets([newWidget, ...shiftedWidgets]);
    setNextId(nextId + 1);
    setTimeout(() => { skipLayoutUpdate.current = false; }, 100);
  };

  const updateWidgetConfig = (widgetId: string, config: any) => {
    setWidgets(widgets.map(w => w.i === widgetId ? {...w, config} : w));
  };

  const removeWidget = (id: string) => {
    setWidgets(widgets.filter(w => w.i !== id));
  };

  const handleSaveDashboard = () => {
    if (!saveDashboardName.trim()) {
      message.error('Dashboard name is required');
      return;
    }

    const dashboardId = saveDashboardName.trim().toLowerCase().replace(/[^a-z0-9]/g, '-');
    const dc: DashboardConfig = {
      id: dashboardId,
      name: saveDashboardName.trim(),
      lastModified: 0,
      widgets: widgets.map(w => ({
        id: w.i,
        type: w.type,
        x: w.x,
        y: w.y,
        w: w.w,
        h: w.h,
        config: w.config
      }))
    };

    SessionStore.getClient().save(dc, SessionStore.getBearer()).then(() => {
      message.success('Dashboard saved successfully');
      setSaveModalVisible(false);
      setSaveDashboardName('');
      setSelectedDashboardId(dashboardId);
      loadDashboards();
    }).catch(() => {
      message.error('Failed to save dashboard');
    });
  };

  const handleDeleteDashboard = () => {
    if (!selectedDashboardId) return;
    SessionStore.getClient().delete(selectedDashboardId, SessionStore.getBearer()).then(() => {
      message.success('Dashboard deleted');
      setSelectedDashboardId(undefined);
      setWidgets([]);
      setNextId(1);
      loadDashboards();
    }).catch(() => {
      message.error('Failed to delete dashboard');
    });
  };

  const handleNewDashboard = () => {
    setSelectedDashboardId(undefined);
    setWidgets([]);
    setNextId(1);
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        breadcrumbRender={() => (
          <Breadcrumb>
            <Breadcrumb.Item>
              <span>Network Server</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>Dashboard</span>
            </Breadcrumb.Item>
          </Breadcrumb>
        )}
        title="Dashboard"
        extra={[
          <Space key="dashboard-controls">
            <Select
              style={{ width: 200 }}
              placeholder="Select dashboard"
              value={selectedDashboardId}
              onChange={(val) => {
                if (val === '__new__') {
                  handleNewDashboard();
                } else {
                  loadDashboard(val);
                }
              }}
              options={[
                ...dashboards.map(d => ({ label: d.name, value: d.id })),
                { label: '+ New Dashboard', value: '__new__' }
              ]}
            />
            <Button icon={<SaveOutlined />} onClick={() => {
              if (selectedDashboardId) {
                const existing = dashboards.find(d => d.id === selectedDashboardId);
                setSaveDashboardName(existing?.name || '');
              }
              setSaveModalVisible(true);
            }}>Save</Button>
            {selectedDashboardId && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDeleteDashboard}>Delete</Button>
            )}
          </Space>
        ]}
      />
      <MetricsAggregations
        loading={false}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        timeInterval={timeInterval}
        setTimeInterval={setTimeInterval}
        metricsAggregator={metricsAggregator}
        setMetricsAggregator={setMetricsAggregator}
        metricsDownsampling={metricsDownsampling}
        setMetricsDownsampling={setMetricsDownsampling}
        loadMetrics={loadMetrics}
        onMetricsAggregationChange={onMetricsAggregationChange}
        onMetricsDownsamplingChange={onMetricsDownsamplingChange}
      />
      <Space>
        <Button onClick={() => addWidget('graph')}>Add Graph Widget</Button>
        <Button onClick={() => addWidget('gauge')}>Add Gauge Widget</Button>
      </Space>
       <div ref={gridContainerRef} style={{ height: `${containerHeight}px`, width: '100%', overflow: 'auto' }}>
         <ResponsiveGridLayout
           rowHeight={rowHeight}
           width={gridWidth}
           onLayoutChange={onLayoutChange}
         >
           {widgets.map(widget => (
             <div key={widget.i} data-grid={{i: widget.i, x: widget.x, y: widget.y, w: widget.w, h: widget.h, minW: 2, minH: 2}} style={{position: 'relative', border: '1px solid #ccc', background: '#fff', overflow: 'hidden'}}>
               <Button size="small" style={{position: 'absolute', top: 0, right: 0, zIndex: 10}} onClick={() => removeWidget(widget.i)}>X</Button>
               <div style={{padding: '8px', height: '100%'}}>
                  {widget.type === 'graph' ? (
                    <GraphWidget
                      config={widget.config}
                      onConfigChange={(config) => updateWidgetConfig(widget.i, config)}
                      startDate={startDate}
                      endDate={endDate}
                      aggregator={metricsAggregator}
                      downsampling={metricsDownsampling}
                      refreshKey={refreshKey}
                    />
                  ) : (
                    <GaugeWidget
                      config={widget.config}
                      onConfigChange={(config) => updateWidgetConfig(widget.i, config)}
                    />
                  )}
               </div>
             </div>
           ))}
         </ResponsiveGridLayout>
       </div>
      <Modal
        title={selectedDashboardId ? "Save Dashboard" : "Save as New Dashboard"}
        open={saveModalVisible}
        onOk={handleSaveDashboard}
        onCancel={() => setSaveModalVisible(false)}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <span>Dashboard Name:</span>
          <Input
            value={saveDashboardName}
            onChange={(e) => setSaveDashboardName(e.target.value)}
            placeholder="Enter dashboard name"
          />
        </Space>
      </Modal>
    </Space>
  );
}

export default Dashboard;
